// My List: private notes as cards, under My Space.
//
// Each note is the owner's alone unless they share it, and a share is
// read-only. A note can be pinned, given a priority, set a reminder (which
// the notification bell shows once it falls due), shared with colleagues, or
// turned into an Action through the ordinary New Action dialog, after which
// the note is ticked off and links to the Action. Server: src/api/routes/myList.js.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import useRefreshOnFocus from '../../hooks/useRefreshOnFocus';
import Modal from '../shared/Modal';
import { NewActionModal } from '../people/MyActionsPage';
import FeatureTour from '../shared/FeatureTour';
import useFeatureTour from '../../hooks/useFeatureTour';
import { MY_LIST_TOUR_ID, myListTourSteps } from './myListTour';
import { fmtDateOnly } from '../../utils/dates';
import {
  PRIORITIES, priorityMeta, sortNotes, isMyListReminderDue, toLocalInput, fromLocalInput,
  localDateOf, reminderPresets, reminderLabel, matchesSearch,
} from '../../utils/myList';

const btn = 'px-2 py-1 text-[10px] font-semibold rounded border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40';
const primary = 'px-3 py-1.5 text-[10px] font-semibold rounded bg-navy text-white hover:bg-navy/80 disabled:opacity-40';
const label = 'block text-[8px] font-bold text-gray-500 uppercase mb-1';

// Drawn rather than emoji, so they look the same whatever fonts a machine has.
const PinIcon = ({ on }) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill={on ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 3h6l-1 6 4 4H6l4-4-1-6z" /><path d="M12 13v8" strokeLinecap="round" />
  </svg>
);
const BellIcon = () => (
  <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="inline -mt-px mr-0.5">
    <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" />
  </svg>
);

const personName = (p) => `${p.first_name || ''} ${p.last_name || ''}`.trim();

// ── Reminder field: a date-time picker with a few quick choices ──────────────
function ReminderField({ value, onChange }) {
  const presets = useMemo(() => reminderPresets(), []);
  return (
    <div>
      <label className={label}>Remind me</label>
      <div className="flex flex-wrap items-center gap-1.5">
        <input type="datetime-local" className="form-input !w-auto" value={value} onChange={e => onChange(e.target.value)} />
        {value && <button type="button" className={btn} onClick={() => onChange('')}>No reminder</button>}
      </div>
      <div className="flex flex-wrap gap-1 mt-1.5">
        {presets.map(p => (
          <button key={p.label} type="button" onClick={() => onChange(p.value)}
            className={`px-2 py-0.5 text-[9px] font-semibold rounded-full border ${value === p.value ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            {p.label}
          </button>
        ))}
      </div>
      <p className="text-[9px] text-gray-400 mt-1">It shows in your notifications at that time, and My List gets a count in the sidebar until you deal with it.</p>
    </div>
  );
}

function PriorityPicker({ value, onChange }) {
  return (
    <div>
      <label className={label}>Priority</label>
      <div className="flex gap-1">
        {PRIORITIES.map(p => (
          <button key={p.id} type="button" onClick={() => onChange(p.id)}
            className={`px-2.5 py-1 text-[10px] font-semibold rounded border ${value === p.id ? `${p.pill} border-transparent ring-1 ring-current` : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}>
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Edit a note ──────────────────────────────────────────────────────────────
function EditNoteModal({ note, onClose, onSaved }) {
  const [title, setTitle] = useState(note.title || '');
  const [body, setBody] = useState(note.body || '');
  const [priority, setPriority] = useState(note.priority || 'normal');
  const [pinned, setPinned] = useState(!!note.pinned);
  const [remind, setRemind] = useState(toLocalInput(note.remind_at));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!title.trim() && !body.trim()) { setError('A note needs a title or some text.'); return; }
    setBusy(true); setError('');
    try {
      await api.put(`my-list/${note.id}`, { title, body, priority, pinned, remind_at: fromLocalInput(remind) });
      onSaved(); onClose();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal title="Edit note" onClose={onClose} footer={<>
      <button onClick={onClose} className={btn}>Cancel</button>
      <button onClick={save} disabled={busy} className={primary}>{busy ? 'Saving…' : 'Save note'}</button>
    </>}>
      {error && <p className="mb-3 text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">{error}</p>}
      <div className="space-y-3">
        <div>
          <label className={label}>Title</label>
          <input autoFocus className="form-input" value={title} onChange={e => setTitle(e.target.value)} maxLength={200} />
        </div>
        <div>
          <label className={label}>Note</label>
          <textarea className="form-input" rows={6} value={body} onChange={e => setBody(e.target.value)} maxLength={5000} />
        </div>
        <PriorityPicker value={priority} onChange={setPriority} />
        <label className="flex items-center gap-2 text-[10px] text-gray-600 cursor-pointer">
          <input type="checkbox" checked={pinned} onChange={e => setPinned(e.target.checked)} />
          Pin to the top of my list
        </label>
        <ReminderField value={remind} onChange={setRemind} />
      </div>
    </Modal>
  );
}

// ── Share a note, read-only ──────────────────────────────────────────────────
function ShareModal({ note, people, currentUser, onClose, onSaved }) {
  const [ids, setIds] = useState(() => (note.shared_with || []).map(s => s.person_id));
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const candidates = people.filter(p => p.id !== currentUser?.id && (!p.employee_status || p.employee_status === 'active'));
  const matches = filter
    ? candidates.filter(p => personName(p).toLowerCase().includes(filter.toLowerCase())).slice(0, 8)
    : [];
  const nameOf = (id) => {
    const p = people.find(x => x.id === id);
    return p ? personName(p) : (note.shared_with || []).find(s => s.person_id === id)?.name || 'Unknown';
  };
  const toggle = (id) => setIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const before = (note.shared_with || []).length;

  const save = async () => {
    setBusy(true); setError('');
    try {
      await api.put(`my-list/${note.id}/shares`, { person_ids: ids });
      onSaved(); onClose();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const verb = ids.length === 0 ? (before ? 'Stop sharing' : 'Share') : 'Share';
  return (
    <Modal title="Share note" onClose={onClose} footer={<>
      <button onClick={onClose} className={btn}>Cancel</button>
      <button onClick={save} disabled={busy || (ids.length === 0 && before === 0)} className={primary}>
        {busy ? 'Saving…' : ids.length ? `${verb} with ${ids.length} ${ids.length === 1 ? 'person' : 'people'}` : verb}
      </button>
    </>}>
      {error && <p className="mb-3 text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">{error}</p>}
      <p className="text-[10px] text-gray-500 mb-3">
        They see it under <span className="font-semibold">Shared with me</span> on their My List and get a notification.
        They can read it but not change it, and your later edits show for them too.
      </p>
      {ids.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {ids.map(id => (
            <span key={id} className="text-[9px] bg-navy/10 text-navy font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-1">
              {nameOf(id)}<button onClick={() => toggle(id)} className="text-navy/50 hover:text-navy" aria-label={`Remove ${nameOf(id)}`}>✕</button>
            </span>
          ))}
        </div>
      )}
      <input autoFocus className="form-input" placeholder="Type a name to add…" value={filter} onChange={e => setFilter(e.target.value)} />
      {matches.length > 0 && (
        <div className="border border-gray-200 rounded mt-1 max-h-48 overflow-y-auto">
          {matches.map(p => (
            <button key={p.id} type="button" onClick={() => { toggle(p.id); setFilter(''); }}
              className="w-full text-left px-2.5 py-1.5 text-[10px] hover:bg-gray-50 flex items-center gap-2">
              <input type="checkbox" readOnly checked={ids.includes(p.id)} />
              {personName(p)}{p.designation && <span className="ml-auto text-[8px] text-gray-400">{p.designation}</span>}
            </button>
          ))}
        </div>
      )}
      {ids.length === 0 && before > 0 && (
        <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-3">
          Nobody is left on the list, so this stops sharing the note. It comes off the {before === 1 ? 'person' : `${before} people`}'s lists.
        </p>
      )}
    </Modal>
  );
}

// ── Delete ───────────────────────────────────────────────────────────────────
function DeleteModal({ note, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const shared = (note.shared_with || []).length;
  const go = async () => {
    setBusy(true); setError('');
    try { await api.delete(`my-list/${note.id}`); onDeleted(); onClose(); }
    catch (e) { setError(e.message); setBusy(false); }
  };
  return (
    <Modal title="Delete this note?" onClose={onClose} footer={<>
      <button onClick={onClose} className={btn}>Keep the note</button>
      <button onClick={go} disabled={busy} className="px-3 py-1.5 text-[10px] font-semibold rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-40">
        {busy ? 'Deleting…' : 'Delete note'}
      </button>
    </>}>
      {error && <p className="mb-3 text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">{error}</p>}
      <p className="text-[11px] text-navy font-semibold mb-1">{note.title || note.body.slice(0, 80)}</p>
      <p className="text-[10px] text-gray-500">
        It cannot be brought back.
        {shared > 0 && ` It is shared with ${shared === 1 ? note.shared_with[0].name : `${shared} people`}, and comes off their list too.`}
        {note.action_id && ' The Action it became is not affected.'}
      </p>
    </Modal>
  );
}

// ── A card ───────────────────────────────────────────────────────────────────
function NoteCard({ note, readOnly, highlight, onEdit, onShare, onDelete, onToAction, onChanged }) {
  const { navigate } = useApp();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const ref = useRef(null);
  const meta = priorityMeta(note.priority);
  const due = !readOnly && isMyListReminderDue(note);
  const done = !!note.done_at;

  useEffect(() => {
    if (highlight && ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlight]);

  const patch = async (fields) => {
    setBusy(true); setError('');
    try { await api.put(`my-list/${note.id}`, fields); onChanged(); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const leave = async () => {
    setBusy(true); setError('');
    try { await api.delete(`my-list/${note.id}/shares/me`); onChanged(); }
    catch (e) { setError(e.message); setBusy(false); }
  };

  return (
    <div ref={ref} data-tour="mylist-card"
      className={`bg-white border border-gray-200 border-l-4 ${meta.edge} rounded-lg p-3 flex flex-col gap-2 shadow-sm ${done ? 'opacity-70' : ''} ${highlight ? 'ring-2 ring-gold' : ''}`}>
      <div className="flex items-start gap-2">
        {!readOnly && (
          <input type="checkbox" checked={done} disabled={busy} onChange={() => patch({ done: !done })}
            className="mt-0.5" title={done ? 'Reopen' : 'Tick off'} aria-label={done ? 'Reopen note' : 'Tick off note'} />
        )}
        <div className="flex-1 min-w-0">
          {note.title && <div className={`text-[12px] font-semibold text-navy break-words ${done ? 'line-through' : ''}`}>{note.title}</div>}
          {readOnly && <div className="text-[9px] text-gray-400">From {note.owner_name || 'a colleague'}</div>}
        </div>
        {!readOnly && (
          <button onClick={() => patch({ pinned: !note.pinned })} disabled={busy}
            className={`leading-none ${note.pinned ? 'text-gold' : 'text-gray-300 hover:text-gray-500'}`}
            title={note.pinned ? 'Unpin' : 'Pin to the top'} aria-label={note.pinned ? 'Unpin' : 'Pin'}><PinIcon on={!!note.pinned} /></button>
        )}
      </div>

      {note.body && (
        <div className={`text-[11px] text-gray-700 whitespace-pre-wrap break-words max-h-48 overflow-y-auto ${done && !note.title ? 'line-through' : ''}`}>{note.body}</div>
      )}

      <div className="flex flex-wrap items-center gap-1">
        <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${meta.pill}`}>{meta.label}</span>
        {note.remind_at && !done && !readOnly && (
          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${due ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}
            title={due ? 'This reminder has come round' : 'Reminder'}>
            <BellIcon />{due ? 'Due ' : ''}{reminderLabel(note.remind_at)}
          </span>
        )}
        {!readOnly && (note.shared_with || []).length > 0 && (
          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-sky-50 text-sky-700"
            title={note.shared_with.map(s => s.name).join(', ')}>
            Shared with {note.shared_with.length === 1 ? note.shared_with[0].name : `${note.shared_with.length} people`}
          </span>
        )}
        {note.action_id && !readOnly && (
          <button onClick={() => navigate('my-tasks', note.action_id)}
            className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 hover:underline"
            title={note.action_title || ''}>
            Action{note.action_status === 'resolved' ? ' (resolved)' : ''} →
          </button>
        )}
        {done && <span className="text-[9px] text-gray-400">Done {fmtDateOnly(note.done_at)}</span>}
      </div>

      {error && <p className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{error}</p>}

      <div className="flex flex-wrap gap-1 pt-1 border-t border-gray-100">
        {readOnly ? (
          <button onClick={leave} disabled={busy} className={btn}>Remove from my list</button>
        ) : (<>
          <button onClick={() => onEdit(note)} className={btn}>Edit</button>
          <button data-tour="mylist-share" onClick={() => onShare(note)} className={btn}>Share</button>
          {!note.action_id && !done && <button data-tour="mylist-action" onClick={() => onToAction(note)} className={btn}>Make an Action</button>}
          {due && <button onClick={() => patch({ remind_at: null })} disabled={busy} className={btn}>Clear reminder</button>}
          <button onClick={() => onDelete(note)} className={`${btn} ml-auto text-red-600`}>Delete</button>
        </>)}
      </div>
    </div>
  );
}

// ── Quick add ────────────────────────────────────────────────────────────────
function QuickAdd({ onAdded }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState('normal');
  const [pinned, setPinned] = useState(false);
  const [remind, setRemind] = useState('');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const add = async () => {
    if (!title.trim() && !body.trim()) return;
    setBusy(true); setError('');
    try {
      await api.post('my-list', { title, body, priority, pinned, remind_at: fromLocalInput(remind) });
      setTitle(''); setBody(''); setPriority('normal'); setPinned(false); setRemind(''); setOpen(false);
      onAdded();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div data-tour="mylist-add" className="bg-white border border-gray-200 rounded-lg p-3 mb-4 shadow-sm">
      <input className="form-input" placeholder="Add a note…" value={title} maxLength={200}
        onFocus={() => setOpen(true)} onChange={e => setTitle(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); add(); } }} />
      {open && (
        <div className="mt-2 space-y-3">
          <textarea className="form-input" rows={3} placeholder="More detail (optional)" value={body} maxLength={5000}
            onChange={e => setBody(e.target.value)} />
          <div className="grid gap-3 md:grid-cols-2">
            <div data-tour="mylist-priority" className="space-y-2">
              <PriorityPicker value={priority} onChange={setPriority} />
              <label className="flex items-center gap-2 text-[10px] text-gray-600 cursor-pointer">
                <input type="checkbox" checked={pinned} onChange={e => setPinned(e.target.checked)} />
                Pin to the top
              </label>
            </div>
            <div data-tour="mylist-reminder"><ReminderField value={remind} onChange={setRemind} /></div>
          </div>
          {error && <p className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">{error}</p>}
          <div className="flex justify-end gap-1.5">
            <button onClick={() => { setOpen(false); setError(''); }} className={btn}>Close</button>
            <button onClick={add} disabled={busy || (!title.trim() && !body.trim())} className={primary}>{busy ? 'Adding…' : 'Add note'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const TABS = [
  { id: 'open', label: 'Open' },
  { id: 'done', label: 'Done' },
  { id: 'shared', label: 'Shared with me' },
];

export default function MyListPage() {
  const { currentUser, detailId } = useApp();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [tab, setTab] = useState('open');
  const [priority, setPriority] = useState('all');
  const [search, setSearch] = useState('');
  const [people, setPeople] = useState([]);
  const [sows, setSows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [converting, setConverting] = useState(null);
  const [linkError, setLinkError] = useState(null);
  const [focusId, setFocusId] = useState(detailId || null);
  useEffect(() => { setFocusId(detailId || null); }, [detailId]);
  // Held back until the list has loaded, so the steps have cards to point at.
  const tour = useFeatureTour(MY_LIST_TOUR_ID, { enabled: !!data });
  // Every step is on the Open tab, where the quick add box and your own cards are.
  useEffect(() => { if (tour.open) { setTab('open'); setPriority('all'); setSearch(''); } }, [tour.open]);

  const load = useCallback(() => {
    api.get('my-list')
      .then(d => { setData({ notes: d?.notes || [], shared: d?.shared || [] }); setLoadError(''); })
      .catch(e => setLoadError(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);
  useRefreshOnFocus(load);
  useEffect(() => {
    api.get('people').then(p => setPeople(Array.isArray(p) ? p : [])).catch(() => {});
  }, []);
  // Only needed once somebody turns a note into an Action (the dialog's SOW picker).
  useEffect(() => {
    if (converting && !sows.length) api.get('sows', { status: 'Active' }).then(s => setSows(Array.isArray(s) ? s : [])).catch(() => {});
  }, [converting, sows.length]);

  // A notification names the note it is about: open the tab it is on.
  useEffect(() => {
    if (!focusId || !data) return;
    const mine = data.notes.find(n => n.id === focusId);
    if (mine) setTab(mine.done_at ? 'done' : 'open');
    else if (data.shared.some(n => n.id === focusId)) setTab('shared');
  }, [focusId, data]);

  const counts = useMemo(() => ({
    open: (data?.notes || []).filter(n => !n.done_at).length,
    done: (data?.notes || []).filter(n => n.done_at).length,
    shared: (data?.shared || []).length,
  }), [data]);
  const due = useMemo(() => (data?.notes || []).filter(n => isMyListReminderDue(n)).length, [data]);

  const shown = useMemo(() => {
    if (!data) return [];
    const base = tab === 'shared' ? data.shared
      : data.notes.filter(n => (tab === 'done' ? !!n.done_at : !n.done_at));
    return sortNotes(base.filter(n => (priority === 'all' || n.priority === priority) && matchesSearch(n, search)));
  }, [data, tab, priority, search]);

  const linkAction = async (note, created) => {
    if (!created?.id) return;
    try {
      await api.post(`my-list/${note.id}/action`, { action_id: created.id });
      setLinkError(null);
    } catch (e) {
      // The Action exists either way, so the dialog closes as raised; the
      // failure is reported on the page rather than inviting a second Action.
      setLinkError(`The Action was raised, but the note could not be linked to it: ${e.message}`);
    }
    load();
  };

  const chooseTab = (id) => { setTab(id); setFocusId(null); };

  return (
    <div className="max-w-6xl">
      <div data-tour="mylist-header" className="mb-4">
        <h1 className="text-lg font-bold text-navy flex items-center gap-2">
          My List
          <button onClick={tour.start} title="Replay the guided tour" aria-label="Replay the guided tour"
            className="w-4 h-4 rounded-full border border-gray-300 text-[9px] text-gray-500 hover:bg-gray-100 leading-none">?</button>
        </h1>
        <p className="text-[11px] text-gray-500">
          Your own notes. Nobody else sees them unless you share one.
          {due > 0 && <span className="ml-1 font-semibold text-amber-700">{due} {due === 1 ? 'reminder is' : 'reminders are'} due.</span>}
        </p>
      </div>

      {tab !== 'shared' && <QuickAdd onAdded={load} />}

      <div data-tour="mylist-filters" className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex rounded border border-gray-200 overflow-hidden">
          {TABS.map(t => (
            <button key={t.id} onClick={() => chooseTab(t.id)}
              className={`px-3 py-1 text-[10px] font-semibold ${tab === t.id ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              {t.label} <span className="opacity-70">{counts[t.id]}</span>
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {[{ id: 'all', label: 'All' }, ...PRIORITIES].map(p => (
            <button key={p.id} onClick={() => { setPriority(p.id); setFocusId(null); }}
              className={`px-2 py-0.5 text-[9px] font-semibold rounded-full border ${priority === p.id ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              {p.label}
            </button>
          ))}
        </div>
        <input className="form-input !w-56 ml-auto" placeholder="Search notes…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {linkError && (
        <p className="mb-3 text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">{linkError}</p>
      )}
      {loadError && !data && (
        <p className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">Your list could not be loaded: {loadError}</p>
      )}
      {!data && !loadError && <p className="text-[11px] text-gray-400">Loading…</p>}

      {data && shown.length === 0 && (
        <div className="text-center text-[11px] text-gray-400 border border-dashed border-gray-200 rounded-lg py-10">
          {search || priority !== 'all' ? 'Nothing matches that filter.'
            : tab === 'open' ? 'Nothing on your list. Add a note above.'
            : tab === 'done' ? 'Notes you tick off land here.'
            : 'Nobody has shared a note with you.'}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 items-start">
        {shown.map(n => (
          <NoteCard key={n.id} note={n} readOnly={tab === 'shared'} highlight={n.id === focusId}
            onEdit={setEditing} onShare={setSharing} onDelete={setDeleting} onToAction={setConverting}
            onChanged={load} />
        ))}
      </div>

      {tour.open && <FeatureTour steps={myListTourSteps} onFinish={tour.finish} onSkip={tour.skip} />}
      {editing && <EditNoteModal note={editing} onClose={() => setEditing(null)} onSaved={load} />}
      {sharing && <ShareModal note={sharing} people={people} currentUser={currentUser} onClose={() => setSharing(null)} onSaved={load} />}
      {deleting && <DeleteModal note={deleting} onClose={() => setDeleting(null)} onDeleted={load} />}
      {converting && (
        <NewActionModal people={people} sows={sows} currentUser={currentUser}
          initial={{
            title: converting.title || converting.body.split('\n')[0].slice(0, 200),
            details: converting.title ? converting.body : converting.body.split('\n').slice(1).join('\n').trim(),
            dueDate: localDateOf(converting.remind_at),
          }}
          onCreated={(created) => linkAction(converting, created)}
          onClose={() => setConverting(null)} />
      )}
    </div>
  );
}
